import { api } from "@/lib/api-client";
import type { Paginated } from "../agents/api";

export type WebhookSubscription = {
  event_type: string;
  enabled: boolean;
};

export type WebhookEndpoint = {
  id: string;
  url: string;
  description?: string;
  enabled: boolean;
  has_secret?: boolean;
  total_deliveries: number;
  failed_deliveries: number;
  subscriptions: WebhookSubscription[];
};

export type EventType = { value: string; label: string };

export type Delivery = {
  id: string;
  event_type: string;
  status: "sent" | "failed" | "dlq" | "pending" | string;
  attempt_count: number;
  response_code?: number | null;
  created_at: string;
  last_attempt_at?: string | null;
};

export const webhooksApi = {
  listEndpoints: async () => {
    const res = await api.get<Paginated<WebhookEndpoint> | WebhookEndpoint[]>(
      "/webhooks/endpoints/",
    );
    return Array.isArray(res.data) ? res.data : res.data.results;
  },
  createEndpoint: async (data: {
    url: string;
    description?: string;
    secret?: string;
    event_types: string[];
  }) => {
    const res = await api.post<WebhookEndpoint>("/webhooks/endpoints/", data);
    return res.data;
  },
  updateEndpoint: async (id: string, data: { enabled?: boolean }) => {
    const res = await api.patch<WebhookEndpoint>(
      `/webhooks/endpoints/${id}/`,
      data,
    );
    return res.data;
  },
  deleteEndpoint: async (id: string) => {
    await api.delete(`/webhooks/endpoints/${id}/`);
  },
  rotateSecret: async (id: string) => {
    const res = await api.post<{ secret: string }>(
      `/webhooks/endpoints/${id}/rotate_secret/`,
    );
    return res.data;
  },
  endpointDeliveries: async (id: string) => {
    const res = await api.get<Paginated<Delivery> | Delivery[]>(
      `/webhooks/endpoints/${id}/deliveries/`,
    );
    return Array.isArray(res.data) ? res.data : res.data.results;
  },
  addSubscriptions: async (endpointId: string, eventTypes: string[]) => {
    await api.post(`/webhooks/endpoints/${endpointId}/subscriptions/add/`, {
      event_types: eventTypes,
    });
  },
  removeSubscription: async (endpointId: string, eventType: string) => {
    await api.delete(
      `/webhooks/endpoints/${endpointId}/subscriptions/${eventType}/`,
    );
  },
  retryDelivery: async (id: string) => {
    await api.post(`/webhooks/deliveries/${id}/retry/`);
  },
  eventTypes: async () => {
    const res = await api.get<Paginated<EventType> | EventType[]>(
      "/webhooks/event-types/",
    );
    return Array.isArray(res.data) ? res.data : res.data.results;
  },
};
